import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPublishedJobsCached,
  selectPublishedJobs,
} from "../../../redux/slices/jobsCacheSlice";
import axiosInstance from "../../../interceptors";

const STAGE_COLORS = ["#9378D6","#7B5AC4","#7C3AED","#10B981"];
const STATUS_PILLS = { Scored:"#ECFDF5 #059669", Completed:"#F0FDF4 #16A34A", "In Progress":"#FFFBEB #D97706", Invited:"#EEF2FF #6A4DB3", Sourced:"#F1F5F9 #64748B" };

const MOCK_CANDIDATES = [
  { id:200, name:"Aarav Sharma", email:"aarav.sharma@gmail.com", stage:"Scored", score:85, applied:"2026-04-02", lastActivity:"2h ago" },
  { id:201, name:"Priya Nair", email:"priya.nair@gmail.com", stage:"Scored", score:91, applied:"2026-04-02", lastActivity:"1h ago" },
  { id:202, name:"Vikram Reddy", email:"vikram.reddy@gmail.com", stage:"Completed", score:72, applied:"2026-04-03", lastActivity:"5h ago" },
  { id:204, name:"Rohit Gupta", email:"rohit.gupta@gmail.com", stage:"In Progress", score:null, applied:"2026-04-05", lastActivity:"3h ago" },
  { id:206, name:"Karthik Menon", email:"karthik.menon@gmail.com", stage:"Invited", score:null, applied:"2026-04-06", lastActivity:"1d ago" },
  { id:208, name:"Arjun Krishnan", email:"arjun.krishnan@gmail.com", stage:"Scored", score:88, applied:"2026-04-01", lastActivity:"30m ago" },
  { id:209, name:"Meera Joshi", email:"meera.joshi@gmail.com", stage:"Completed", score:76, applied:"2026-04-03", lastActivity:"6h ago" },
  { id:232, name:"Akash Pandey", email:"akash.pandey@gmail.com", stage:"Invited", score:null, applied:"2026-04-07", lastActivity:"2d ago" },
  { id:220, name:"Suresh Kumar", email:"suresh.kumar@gmail.com", stage:"Scored", score:82, applied:"2026-04-02", lastActivity:"4h ago" },
  { id:214, name:"Rahul Singh", email:"rahul.singh@gmail.com", stage:"In Progress", score:null, applied:"2026-04-04", lastActivity:"8h ago" },
];

const Pipeline = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const publishedJobs = useSelector(selectPublishedJobs);
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("pipeline");
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  useEffect(() => {
    document.title = "Pipeline - Ample Hire";
    if (!jobId) return;
    dispatch(fetchPublishedJobsCached());
  }, [dispatch, jobId]);

  useEffect(() => {
    if (!jobId) return;
    setFunnelLoading(true);
    axiosInstance
      .get(`/job-posting/jobs/${jobId}/funnel?period=30d`)
      .then((r) => setFunnel(r.data || null))
      .catch(() => setFunnel(null))
      .finally(() => setFunnelLoading(false));
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    const found = (publishedJobs || []).find(j => String(j.jobId) === String(jobId));
    if (found) {
      setJob({
        id: found.jobId,
        title: found.positionName,
        location: (found.locations || []).join(", "),
        openings: found.positionCounts,
        minExp: found.minimumExperience,
        maxExp: found.maximumExperience,
      });
    } else if (publishedJobs && publishedJobs.length > 0) {
      // Fetch completed but no match — show a benign fallback.
      setJob({ id: jobId, title: "Pipeline", location: "", openings: 5 });
    }
  }, [jobId, publishedJobs]);

  if (!jobId) {
    return (
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"24px 32px", fontFamily:"'Inter',sans-serif" }}>
        <h1 style={{ fontSize:22, fontWeight:600, color:"#0F172A", margin:"0 0 4px" }}>Pipeline</h1>
        <p style={{ fontSize:13, color:"#64748B", margin:"0 0 24px" }}>Select a job to view its pipeline</p>
        <div className="bg-white border border-slate-200 rounded-xl py-16 px-6 text-center">
          <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
          <div className="text-base font-semibold text-vf-base mb-1">No job selected</div>
          <div className="text-sm text-vf-muted max-w-md mx-auto mb-5">
            Choose a job from the jobs list to see its candidate pipeline and funnel.
          </div>
          <button
            onClick={() => navigate("/admin/jobs")}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium"
          >
            Go to Jobs
          </button>
        </div>
      </div>
    );
  }

  if (!job) return <div style={{ padding:40, textAlign:"center", color:"#94A3B8" }}>Loading...</div>;

  // Backend stages: Applied → Reviewed → Shortlisted → Converted, plus rejected aside.
  const stages = funnel?.stages || [];
  const counts = stages.map((s) => Number(s.count) || 0);
  const stageNames = stages.map((s) => s.stage);
  const maxCount = Math.max(...counts, 1);
  const totalApplied = counts[0] || 0;
  const rejected = Number(funnel?.rejected) || 0;
  const alerts = funnel?.alerts || [];

  const stats = [
    { label: "Applied", val: counts[0] ?? 0 },
    { label: "Reviewed", val: counts[1] ?? 0 },
    { label: "Shortlisted", val: counts[2] ?? 0 },
    { label: "Converted", val: counts[3] ?? 0 },
  ];

  return (
    <div style={{ maxWidth:1100, margin:"0 auto", padding:"24px 32px", fontFamily:"'Inter',sans-serif" }}>
      {/* Back */}
      <button onClick={() => navigate("/admin/jobs")} style={{ fontSize:13, color:"#7B5AC4", cursor:"pointer", background:"none", border:"none", marginBottom:12, display:"inline-flex", alignItems:"center", gap:4, fontWeight:500 }}>
        &#8592; Back to jobs
      </button>

      {/* Header */}
      <h1 style={{ fontSize:22, fontWeight:600, color:"#0F172A", margin:"0 0 4px" }}>{job.title}</h1>
      <p style={{ fontSize:13, color:"#64748B", margin:"0 0 20px" }}>
        {job.location || "Remote"} &middot; {job.openings} openings
        {job.minExp ? ` \u00b7 ${job.minExp}-${job.maxExp}y exp` : ""}
      </p>

      {/* Tabs */}
      <div style={{ display:"flex", gap:0, borderBottom:"1px solid #E2E8F0", marginBottom:24 }}>
        {["pipeline","candidates"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding:"10px 20px", fontSize:13, fontWeight:tab===t?600:500, cursor:"pointer", color:tab===t?"#7B5AC4":"#64748B", borderBottom:tab===t?"2px solid #7B5AC4":"2px solid transparent", background:"none", border:"none", borderBottomWidth:2, borderBottomStyle:"solid", borderBottomColor:tab===t?"#7B5AC4":"transparent", marginBottom:-1 }}>
            {t === "pipeline" ? "Pipeline" : `Candidates (${MOCK_CANDIDATES.length})`}
          </button>
        ))}
      </div>

      {tab === "pipeline" ? (
        <>
          {/* Stats — stacks 2-col on mobile, 4-col desktop. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-6">
            {stats.map(s => (
              <div key={s.label} style={{ background:"#F8FAFC", borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:24, fontWeight:700, color:"#0F172A" }}>{s.val}</div>
                <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Bottleneck alerts */}
          {alerts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {alerts.map((a, i) => (
                <div key={i} style={{
                  background: a.severity === "error" ? "#FEE2E2" : "#FEF3C7",
                  color: a.severity === "error" ? "#991B1B" : "#92400E",
                  border: `1px solid ${a.severity === "error" ? "#FCA5A5" : "#FDE68A"}`,
                  padding: "10px 14px", borderRadius: 8, fontSize: 12, marginBottom: 8,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <span style={{ fontSize: 14 }}>{a.severity === "error" ? "🚨" : "⚠️"}</span>
                  <b>{a.stage}:</b> {a.message}
                </div>
              ))}
            </div>
          )}

          {funnelLoading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", background: "#F8FAFC", borderRadius: 10 }}>
              Loading funnel…
            </div>
          ) : totalApplied === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", background: "#F8FAFC", borderRadius: 10 }}>
              No applications captured in the last 30 days. Once candidates apply via the career page, the funnel populates here.
            </div>
          ) : (
            <>
              {/* Funnel bars */}
              <div style={{ display:"flex", gap:4, alignItems:"flex-end", height:180, marginBottom:8, padding:"0 8px" }}>
                {stages.map((s, i) => {
                  const c = Number(s.count) || 0;
                  const h = Math.max((c / maxCount) * 160, 28);
                  const isBottleneck = !!s.flagBottleneck;
                  return (
                    <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end" }}>
                      <div style={{
                        width:"100%", height:h, borderRadius:"8px 8px 0 0",
                        background: STAGE_COLORS[i] || STAGE_COLORS[STAGE_COLORS.length - 1],
                        display:"flex", alignItems:"center", justifyContent:"center",
                        transition:"height 0.5s ease",
                        boxShadow: isBottleneck ? "0 0 0 2px #DC2626 inset" : "none",
                      }}>
                        <span style={{ fontSize:18, fontWeight:600, color:"white" }}>{c}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"flex", gap:4, padding:"0 8px", marginBottom:32 }}>
                {stages.map((s, i) => {
                  const conv = i === 0 ? 100 : (counts[i-1] ? Math.round((counts[i] / counts[i-1]) * 100) : 0);
                  const drop = Number(s.dropoffPct) || 0;
                  return (
                    <div key={s.stage} style={{ flex:1, textAlign:"center", paddingTop:8 }}>
                      <div style={{ fontSize:11, fontWeight:500, color: s.flagBottleneck ? "#DC2626" : "#64748B", textTransform:"uppercase", letterSpacing:0.5 }}>
                        {s.stage}
                      </div>
                      <div style={{ fontSize:10, color:"#94A3B8" }}>
                        {i === 0 ? "—" : `${conv}% conv. · ${drop.toFixed(1)}% drop`}
                      </div>
                    </div>
                  );
                })}
              </div>
              {rejected > 0 && (
                <div style={{ marginBottom: 24, fontSize: 11, color: "#94A3B8", textAlign: "right" }}>
                  Rejected (side branch): <b style={{ color: "#475569" }}>{rejected}</b>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        /* Candidates Table */
        <div style={{ border:"1px solid #E2E8F0", borderRadius:12, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:"#F8FAFC" }}>
                {["Candidate","Stage","Score","Applied","Last Activity","Action"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"12px 16px", fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:0.8, borderBottom:"2px solid #E2E8F0" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_CANDIDATES.map(c => {
                const [bg, fg] = (STATUS_PILLS[c.stage] || "F1F5F9 64748B").split(" ");
                return (
                  <tr key={c.id} style={{ borderBottom:"1px solid #F1F5F9" }}
                    onMouseEnter={e => e.currentTarget.style.background="rgba(99,102,241,0.03)"}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <td style={{ padding:"12px 16px" }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#0F172A" }}>{c.name}</div>
                      <div style={{ fontSize:11, color:"#94A3B8" }}>{c.email}</div>
                    </td>
                    <td style={{ padding:"12px 16px" }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:100, background:`${bg}`, color:`${fg}` }}>{c.stage}</span>
                    </td>
                    <td style={{ padding:"12px 16px", fontSize:14, fontWeight:c.score?600:400, color:c.score?"#0F172A":"#CBD5E1" }}>
                      {c.score ? `${c.score}/100` : "—"}
                    </td>
                    <td style={{ padding:"12px 16px", fontSize:12, color:"#64748B" }}>{c.applied}</td>
                    <td style={{ padding:"12px 16px", fontSize:12, color:"#94A3B8" }}>{c.lastActivity}</td>
                    <td style={{ padding:"12px 16px" }}>
                      <button style={{ fontSize:11, padding:"4px 12px", borderRadius:6, border:"1px solid #E2E8F0", background:"white", cursor:"pointer", color:"#64748B", fontWeight:500 }}>
                        {c.stage === "Invited" ? "Remind" : "View"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Pipeline;
